"use client";

import { Header } from "@/components/ui/Header";
import { Footer } from "@/components/ui/Footer";
import { Hero } from "./Hero";
import { HowItWorks } from "./HowItWorks";
import { StatsSection } from "./StatsSection";
import { FeaturesV2 } from "./FeaturesV2";
import { PersonasV2 } from "./PersonasV2";
import { PricingV2 } from "./PricingV2";
import { TestimonialsV2 } from "./TestimonialsV2";
import { CTAV2 } from "./CTAV2";
import { SeoTextBlockV2 } from "./SeoTextBlockV2";
import { QAItem } from "@/components/ui/QAItem";
import type { ScriptarioSeoLocale, ScriptarioSeoPageEntry } from "@/data/scriptario-seo-pages";
import type { ReactNode } from "react";
import dynamic from "next/dynamic";

const CookieConsent = dynamic(() => import("@/components/CookieConsent"));

const FONT_STYLE = {
  fontFamily: "var(--font-commissioner)",
  fontVariationSettings: '"FLAR" 0, "VOLM" 0',
} as const;

const faqCopy: Record<ScriptarioSeoLocale, { title: string; subtitle: string }> = {
  de: { title: "Häufig gestellte Fragen", subtitle: "Antworten auf wichtige Fragen zum Dienst und zur Nutzung" },
  en: {
    title: "Frequently Asked Questions",
    subtitle: "Answers to key questions about the service and workflow",
  },
  es: {
    title: "Preguntas frecuentes",
    subtitle: "Respuestas clave sobre el servicio y el proceso de trabajo",
  },
  "pt-br": {
    title: "Perguntas frequentes",
    subtitle: "Respostas essenciais sobre o serviço e o fluxo de trabalho",
  },
  it: {
    title: "Domande frequenti",
    subtitle: "Risposte alle domande principali sul servizio e sul flusso di lavoro",
  },
};

function FAQSeo({
  items,
  locale,
}: {
  items: ScriptarioSeoPageEntry["faq"];
  locale: ScriptarioSeoLocale;
}) {
  const copy = faqCopy[locale];

  return (
    <section id="faq" className="relative flex flex-col items-center gap-[40px] py-[80px] overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 62% 250% at 50% 50%, var(--brand-primary-soft) 0%, rgba(255,255,255,0) 72%)",
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent 0px, #000 220px, #000 calc(100% - 160px), transparent 100%)",
          maskImage:
            "linear-gradient(to bottom, transparent 0px, #000 220px, #000 calc(100% - 160px), transparent 100%)",
        }}
      />

      <div className="relative flex flex-col gap-[11px] items-center px-4">
        <h2
          className="font-bold not-italic text-[40px] leading-[48px] tracking-[-0.8px] text-text max-tablet:text-[32px] max-tablet:leading-[40px]"
          style={FONT_STYLE}
        >
          {copy.title}
        </h2>
        <p
          className="font-medium not-italic text-[20px] leading-[28px] tracking-[-0.4px] text-text-muted text-center max-w-[624px] max-tablet:text-[18px]"
          style={FONT_STYLE}
        >
          {copy.subtitle}
        </p>
      </div>

      <div className="relative flex flex-col gap-[16px] w-full max-w-[932px] px-[16px] max-desktop:px-[40px] max-tablet:px-[20px]">
        {items.map((item) => (
          <QAItem key={item.question} question={item.question} answer={item.answer} />
        ))}
      </div>
    </section>
  );
}

export function ScriptarioSeoLanding({
  page,
  locale,
  relatedSection,
}: {
  page: ScriptarioSeoPageEntry;
  locale: ScriptarioSeoLocale;
  relatedSection?: ReactNode;
}) {
  return (
    <div className="min-h-screen bg-white">
      <Header isLanding />

      <Hero title={page.seoTitle} subtitle={page.seoSubtitle} uploadTab={page.uploadTab} />

      <HowItWorks />
      <StatsSection />
      <FeaturesV2 />
      <PersonasV2 />
      <PricingV2 />
      <TestimonialsV2 />

      <SeoTextBlockV2 sections={page.seoText} />
      <FAQSeo items={page.faq} locale={locale} />
      {relatedSection}
      <CTAV2 />
      <Footer />

      <CookieConsent />
    </div>
  );
}
